import { stringifyForSSR } from '@confluence/ssr-utilities/entry-points/stringifyForSSR.ts';

type RenderCallback = () => string;

const pending: RenderCallback[] = [];
function queueRenderCallback(callback: RenderCallback): void {
	pending.push(callback);
}

let inject = queueRenderCallback;
let selfRemoveScripts = false;

export const STREAM_REGISTER = '__sR';
export const STREAM_EVENT = '__sE';

/**
 * Bootstrap script that is rendered inline before any data streams are emitted
 *
 * This initializes the following:
 * * __s - A map of stream names to a list of emitted values
 * * __sL - A map of stream names to a set of listeners
 * * __sR - A function that initializes a stream if it doesn't exist
 * * __sE - A function that emits a value to a stream
 * * __sC - A function that inserts an emotion style tag into the head. Self-deleting to avoid hydration mismatches.
 */
/* istanbul ignore next */
const streamBootstrapFn = (window, STREAM_REGISTER_KEY, STREAM_EVENT_KEY) => {
	if (!window.__s) {
		window.__s = {};
	}
	if (!window.__sL) {
		window.__sL = {};
	}
	window[STREAM_REGISTER_KEY] = (s) => {
		if (!window.__s[s]) {
			window.__s[s] = [];
		}
		if (!window.__sL[s]) {
			window.__sL[s] = new Set();
		}
	};
	window[STREAM_EVENT_KEY] = (s, v) => {
		try {
			if (!window.__s[s]) {
				window.__s[s] = [];
			}
			window.__s[s].push(v);
			if (window.__sL[s]) {
				window.__sL[s].forEach((fn) => fn(v));
			}
		} catch (e) {
			// eslint-disable-next-line no-console
			console.error(`[${STREAM_EVENT_KEY}] error`, e);
		}
	};
};

export function getStreamInject() {
	return inject;
}

/**
 * Set the function to use for injecting content into the SSR stream
 * @param callback - The callback to set the inject function to
 *
 * This will immediately emit any pending callbacks
 */
export function setStreamInject(
	callback: (cb: RenderCallback) => void,
	options?: { selfRemoveScripts?: boolean },
): void {
	inject = callback;
	selfRemoveScripts = options?.selfRemoveScripts ?? false;

	if (pending.length > 0) {
		pending.forEach(inject);
		pending.length = 0;
	}
}

export function clearStreamInject(): void {
	inject = queueRenderCallback;
	selfRemoveScripts = false;
}

const streams = new Map<string, EventStream<any>>();
/**
 * Clear all streams
 */
export function clearServerStreams(): void {
	streams.clear();
}

class EventStream<T extends object> {
	private initialized = false;
	private stream: string;

	constructor(stream: string) {
		this.stream = stream;
	}

	private executeInject(finalResult: T) {
		inject(() => {
			const script = [] as string[];
			if (!this.initialized) {
				script.push(`${STREAM_REGISTER}("${this.stream}")`);
				this.initialized = true;
			}
			script.push(`${STREAM_EVENT}("${this.stream}", ${stringifyForSSR(finalResult)})`);
			const suffix = selfRemoveScripts ? ';document.currentScript?.remove()' : '';
			return `${script.join(';')}${suffix}`;
		});
	}

	emit(value: T | (() => Promise<T> | T)): void {
		const maybeResult = typeof value === 'function' ? value() : value;
		if (typeof (maybeResult as any)?.then === 'function') {
			void (maybeResult as Promise<T>).then((result) => {
				if (result) {
					this.executeInject(result);
				}
			});
		} else {
			if (maybeResult) {
				this.executeInject(maybeResult as T);
			}
		}
	}
}

export function getServerStream<T extends object>(key: string): EventStream<T> {
	if (streams.size === 0) {
		inject(
			() => `(${streamBootstrapFn.toString()})(window, "${STREAM_REGISTER}", "${STREAM_EVENT}")`,
		);
	}

	let stream = streams.get(key);
	if (!stream) {
		stream = new EventStream(key);
		streams.set(key, stream);
	}
	return stream;
}
